import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ClipboardCheck, Clock, AlertTriangle, CheckCircle, 
  User, Calendar, Play, Eye, ChevronRight, RefreshCw
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverAudits = () => {
  const navigate = useNavigate();
  const [pendingAudits, setPendingAudits] = useState([]);
  const [completedAudits, setCompletedAudits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate]);

  const getObserverId = () => {
    const user = localStorage.getItem('observer_user');
    if (user) return JSON.parse(user).id;
    return null;
  };

  const loadData = async () => {
    try {
      const observerId = getObserverId();
      if (!observerId) return;

      const [pendingRes, historyRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/audit/pending/${observerId}`),
        axios.get(`${BACKEND_URL}/api/audit/observer/${observerId}/history`),
        axios.get(`${BACKEND_URL}/api/audit/observer/${observerId}/stats`)
      ]);

      setPendingAudits(pendingRes.data.pending_audits || []);
      setCompletedAudits(historyRes.data.audits || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading audits:', error);
    } finally {
      setLoading(false);
    }
  };

  const startAudit = async (auditId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/audit/${auditId}/start`);
      navigate(`/observer/audit/${auditId}`);
    } catch (error) {
      console.error('Error starting audit:', error);
      alert('Failed to start audit');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'assigned':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Assigned</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">In Progress</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Completed</span>;
      case 'disputed':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Disputed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">{status}</span>;
    }
  };

  const getOutcomeBadge = (outcome) => {
    switch (outcome) {
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Approved</span>;
      case 'needs_improvement':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">Needs Improvement</span>;
      case 'flagged':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Flagged</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Peer Audits</h1>
              <p className="text-gray-600">Review sessions from other observers</p>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4 text-center">
                  <ClipboardCheck className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{stats.total_audits || 0}</p>
                  <p className="text-sm text-gray-500">Total Audits</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{stats.approved_count || 0}</p>
                  <p className="text-sm text-gray-500">Approved</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{stats.needs_improvement_count || 0}</p>
                  <p className="text-sm text-gray-500">Needs Improvement</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl mb-2">⭐</div>
                  <p className="text-2xl font-bold text-gray-900">{stats.average_score || 0}</p>
                  <p className="text-sm text-gray-500">Avg Score</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'pending' ? 'default' : 'outline'}
              onClick={() => setActiveTab('pending')}
              className={activeTab === 'pending' ? 'bg-violet-600' : ''}
            >
              <Clock className="w-4 h-4 mr-2" />
              Pending ({pendingAudits.length})
            </Button>
            <Button
              variant={activeTab === 'completed' ? 'default' : 'outline'}
              onClick={() => setActiveTab('completed')}
              className={activeTab === 'completed' ? 'bg-violet-600' : ''}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Completed ({completedAudits.length})
            </Button>
          </div>

          {/* Pending Audits */}
          {activeTab === 'pending' && (
            <div className="space-y-4">
              {pendingAudits.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <ClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Audits</h3>
                    <p className="text-gray-600">You&apos;re all caught up! Check back later for new audits.</p>
                  </CardContent>
                </Card>
              ) : (
                pendingAudits.map((audit) => (
                  <Card key={audit.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-violet-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{audit.child_name || 'Child Session'}</h3>
                            <p className="text-sm text-gray-500">
                              Observer: {audit.original_observer_name}
                            </p>
                            <div className="flex items-center gap-3 mt-1">
                              {getStatusBadge(audit.status)}
                              <span className="text-xs text-gray-400">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Due: {formatDate(audit.due_date)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {audit.priority === 'high' && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">High Priority</span>
                          )}
                          <Button
                            onClick={() => startAudit(audit.id)}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                          >
                            {audit.status === 'in_progress' ? (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Continue
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-2" />
                                Start Audit
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Completed Audits */}
          {activeTab === 'completed' && (
            <div className="space-y-4">
              {completedAudits.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Completed Audits</h3>
                    <p className="text-gray-600">Completed audits will appear here.</p>
                  </CardContent>
                </Card>
              ) : (
                completedAudits.map((audit) => (
                  <Card key={audit.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-gray-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{audit.child_name || 'Child Session'}</h3>
                            <p className="text-sm text-gray-500">
                              Observer: {audit.original_observer_name}
                            </p>
                            <div className="flex items-center gap-3 mt-1">
                              {getOutcomeBadge(audit.audit_result?.outcome)}
                              <span className="text-xs text-gray-400">
                                Score: {audit.audit_result?.scores?.average || '-'}/10
                              </span>
                              <span className="text-xs text-gray-400">
                                {formatDate(audit.completed_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/observer/audit/${audit.id}/view`)}
                        >
                          View Details
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObserverAudits;
